export const treatAsUTC = (date: Date | string | number) => {
  const result = new Date(date);
  result.setMinutes(result.getMinutes() - result.getTimezoneOffset());
  return result;
};

export const daysSince = (startDate: Date | string | number) => {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  return (
    (treatAsUTC(Date.now()).getTime() - treatAsUTC(startDate).getTime()) /
    millisecondsPerDay
  );
};

export const minutesSince = (startDate: Date) => {
  const millisecondsPerMinute = 60 * 1000;
  const minutesSinceDate =
    (treatAsUTC(Date.now()).getTime() - treatAsUTC(startDate).getTime()) /
    millisecondsPerMinute;
  return Number(minutesSinceDate).toFixed(0);
};

export const createBrowserFriendlyDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString(undefined, {
    timeZone: 'UTC',
  });
};

export const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const MONTH_SHORTHANDS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

export const makeMDYDateFromAnyDate = (date: Date | string) => {
  const d = new Date(date);

  if (isNaN(d.getTime())) {
    return null;
  }

  return `${monthNames[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

export const isGreaterThanOneHourAgo = (timestamp: Date | string) => {
  const oneHour = 60 * 60 * 1000; /* ms */
  return new Date().getTime() - new Date(timestamp).getTime() > oneHour;
};

const isPlural = (num: number) => (num > 1 ? 's' : '');
const isRelevant = (num: number, text: string) =>
  num > 0 ? `${num} ${text}${isPlural(num)}` : '';

// Average days per month (365.25 / 12)
const DAYS_PER_MONTH = 30.4375;

export const translateMonthsToMonthYearFormat = (months: number) => {
  // If less than 1 month, convert to days
  if (months < 1) {
    const days = Math.round(months * DAYS_PER_MONTH);
    return `${days} day${days !== 1 ? 's' : ''}`;
  }

  const years = Math.trunc(months / 12);
  const monthsRemaining = months % 12;
  return `${isRelevant(years, 'year')}${
    years > 0 && monthsRemaining > 0 ? ' and ' : ''
  }${isRelevant(monthsRemaining, 'month')}`;
};

/**
 * Formats tonnes for gift displays. Shows kg when value is < 0.1 tonnes.
 * e.g. 0.05 → "50 kg", 1.5 → "1.5 tonnes"
 */
export const formatTonnesOrKgForGift = (tonnes: number): string => {
  if (tonnes < 0.1) {
    const kg = Math.round(tonnes * 1000);
    return `${kg} kg`;
  }
  const formatted = tonnes % 1 === 0 ? tonnes.toString() : tonnes.toFixed(1);
  return `${formatted} tonne${tonnes !== 1 ? 's' : ''}`;
};

/**
 * Formats months for gift displays (e.g., "X month(s) carbon neutral")
 * Converts to days if under 1 month
 */
export const formatMonthsOrDaysForGift = (months: number) => {
  if (months < 1) {
    const days = Math.round(months * DAYS_PER_MONTH);
    return `${days} day${days !== 1 ? 's' : ''}`;
  }
  const roundedMonths = Math.round(months * 10) / 10;
  return `${roundedMonths} month${roundedMonths !== 1 ? 's' : ''}`;
};

/** Default annual footprint (tonnes CO₂e) when country/lifestyle unknown (e.g. US average). */
const DEFAULT_ANNUAL_FOOTPRINT_TONNES = 16;

/**
 * Returns human-readable "weeks of a typical person's emissions" for gift impact.
 * Uses annualFootprint when > 0, otherwise DEFAULT_ANNUAL_FOOTPRINT_TONNES.
 */
export function getWeeksOfEmissionsText(
  tonnes: number,
  annualFootprint: number
): string {
  if (tonnes <= 0) return '';
  const effectiveAnnual =
    annualFootprint > 0 ? annualFootprint : DEFAULT_ANNUAL_FOOTPRINT_TONNES;
  const weeks = (tonnes / effectiveAnnual) * 52;
  if (weeks < 1) return 'less than 1 week';
  const w = Math.round(weeks);
  if (w <= 1) return 'about 1 week';
  if (w <= 4) return `about ${w - 1}-${w} weeks`;
  if (w < 52) return `about ${Math.round(weeks / 4)} months`;
  const years = Math.round(weeks / 52);
  return `about ${years} year${years !== 1 ? 's' : ''}`;
}

export function timeSince(date: Date) {
  const seconds = Math.max(
    0,
    Math.floor((new Date().getTime() - date.getTime()) / 1000)
  );

  const units: Array<[number, string]> = [
    [31536000, 'year'],
    [2592000, 'month'],
    [86400, 'day'],
    [3600, 'hour'],
    [60, 'minute'],
  ];

  for (const [unitSeconds, unitName] of units) {
    const interval = Math.floor(seconds / unitSeconds);
    if (interval >= 1) {
      return `${interval} ${unitName}${interval === 1 ? '' : 's'}`;
    }
  }

  return `${seconds} second${seconds === 1 ? '' : 's'}`;
}

export function wait(ms: number) {
  return new Promise((res) => setTimeout(res, ms));
}

export function todaysDate() {
  return new Date().toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

export function makeVerboseDate(date: Date) {
  return date.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    day: 'numeric',
  });
}

export function makeConciseDate(date: Date) {
  return date.toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });
}

export function daysUntilDate(date: Date) {
  const today = new Date();
  const diffTime = date.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}
